import { Link } from "react-router-dom";

const AreaRow = ({ a, max, idx }) => {
  const pct = (n) => (a.count ? (n / a.count) * 100 : 0);
  const width = max ? Math.max((a.count / max) * 100, 8) : 0;
  return (
    <div className="py-2 border-b border-white/5 last:border-0" data-testid={`signals-area-${idx}`}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm text-white/85 truncate">{a.area}</span>
        <span className="text-[11px] font-mono text-white/45 shrink-0">
          {a.count} bill{a.count === 1 ? "" : "s"}
          {a.up + a.down > 0 && (
            <span className={a.avgDelta >= 0 ? " text-emerald-300" : " text-rose-300"}> · avg {a.avgDelta > 0 ? "+" : ""}{a.avgDelta} pp</span>
          )}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-white/[0.06] mt-1.5 overflow-hidden" style={{ width: `${width}%` }}>
        <div className="flex h-full">
          <div className="h-full bg-emerald-400" style={{ width: `${pct(a.up)}%` }} />
          <div className="h-full bg-rose-400" style={{ width: `${pct(a.down)}%` }} />
          <div className="h-full bg-purple-400/70" style={{ width: `${pct(a.stalled)}%` }} />
        </div>
      </div>
    </div>
  );
};

export const PolicyShiftPanel = ({ overview }) => {
  const areas = overview?.byPolicyArea || [];
  const drivers = overview?.drivers || [];
  const movers = overview?.recentMovers || [];
  const maxCount = areas[0]?.count || 0;
  const maxDriver = drivers[0]?.count || 0;
  return (
    <div className="space-y-4">
      <div className="whip-surface rounded-xl border border-white/10 p-4" data-testid="signals-policy-panel">
        <h3 className="text-[11px] font-mono font-bold uppercase tracking-[0.2em] text-white/60">Where the floor is shifting</h3>
        <p className="text-xs text-white/45 mt-1 mb-2">Signals grouped by policy area. Bars split surges, drops and stalls.</p>
        <div className="flex gap-3 text-[10px] font-mono text-white/45 mb-1">
          <span className="inline-flex items-center gap-1"><i className="w-2 h-2 rounded-full bg-emerald-400 inline-block" /> up</span>
          <span className="inline-flex items-center gap-1"><i className="w-2 h-2 rounded-full bg-rose-400 inline-block" /> down</span>
          <span className="inline-flex items-center gap-1"><i className="w-2 h-2 rounded-full bg-purple-400/70 inline-block" /> stalled</span>
        </div>
        {areas.length === 0 ? (
          <p className="text-sm text-white/45 py-3">No signals for these filters.</p>
        ) : areas.map((a, i) => <AreaRow key={a.area} a={a} max={maxCount} idx={i} />)}
      </div>

      <div className="whip-surface rounded-xl border border-white/10 p-4" data-testid="signals-drivers-panel">
        <h3 className="text-[11px] font-mono font-bold uppercase tracking-[0.2em] text-white/60">What is moving the numbers</h3>
        <p className="text-xs text-white/45 mt-1 mb-3">How often each trigger shows up behind the current signals.</p>
        {drivers.length === 0 ? (
          <p className="text-sm text-white/45">No drivers recorded.</p>
        ) : drivers.map((d, i) => (
          <div key={d.label} className="flex items-center gap-3 py-1" data-testid={`signals-driver-${i}`}>
            <span className="text-sm text-white/80 w-40 truncate">{d.label}</span>
            <div className="flex-1 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
              <div className="h-full bg-orange-400" style={{ width: `${maxDriver ? (d.count / maxDriver) * 100 : 0}%` }} />
            </div>
            <span className="text-[11px] font-mono text-white/55 w-6 text-right">{d.count}</span>
          </div>
        ))}
      </div>

      {movers.length > 0 && (
        <div className="whip-surface rounded-xl border border-white/10 p-4" data-testid="signals-movers-panel">
          <h3 className="text-[11px] font-mono font-bold uppercase tracking-[0.2em] text-white/60">Moved in the last 30 days</h3>
          <p className="text-xs text-white/45 mt-1 mb-2">Any tracked forecast that changed by a point or more, regardless of size.</p>
          <ul className="divide-y divide-white/5">
            {movers.map((m, i) => (
              <li key={m.billId}>
                <Link to={`/bill/${m.billId}`} className="flex items-center justify-between gap-3 py-2 hover:text-orange-200" data-testid={`signals-mover-${i}`}>
                  <span className="min-w-0 flex-1">
                    <span className="block font-mono text-xs text-orange-200 whitespace-nowrap">{m.billNumber}</span>
                    <span className="block text-sm text-white/80 truncate">{m.billTitle}</span>
                  </span>
                  <span className={`font-mono text-sm font-bold shrink-0 whitespace-nowrap ${m.deltaPP >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                    {m.deltaPP > 0 ? "+" : ""}{m.deltaPP} pp
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
